"use client";

import { useCallback, useMemo } from "react";
import {
  SourceCitationBuilder,
  type SourceField,
  type SourceValues,
} from "@/components/tool-kit/source-citation-builder";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  UN_DOCUMENT_FIELDS as F,
  formatUnDocument,
  type UnDocumentLabels,
  unDocumentIsReady,
} from "@/lib/tool-kit/legal-sources/un-document-citation-generator";

/** English strings. A translated content file overrides these through `ui`. */
export const UN_DOCUMENT_CITATION_GENERATOR_STRINGS = {
  formHeading: "Document details",
  resultsHeading: "Your citations",
  emptyPrompt:
    "Enter the resolution number or document symbol and the date to see your citations.",
  copy: "Copy",
  copied: "Copied!",
  tryExample: "Try an example",
  clear: "Clear",
  bodyLabel: "Issuing body",
  bodyGa: "General Assembly",
  bodySc: "Security Council",
  bodyEcosoc: "Economic and Social Council",
  bodyHrc: "Human Rights Council",
  bodySg: "Secretary-General",
  kindLabel: "Type of document",
  kindResolution: "Resolution",
  kindDocument: "Report, letter or other document",
  numberLabel: "Resolution number",
  numberPlaceholder: "70/1",
  numberHint:
    "As the resolution names itself: 70/1 for the General Assembly, 2334 for the Security Council.",
  titleLabel: "Title (leave empty for an untitled resolution)",
  titlePlaceholder:
    "Transforming our world: the 2030 Agenda for Sustainable Development",
  titleHint:
    "Copy it as the document prints it. MLA and Chicago get title case automatically.",
  shortTitleLabel: "Short title for MLA in-text citations (optional)",
  shortTitlePlaceholder: "Transforming our world",
  symbolLabel: "Document symbol",
  symbolPlaceholder: "A/RES/70/1",
  symbolHint:
    "Printed at the top right of the first page. Security Council symbols include the year: S/RES/2334 (2016).",
  dateLabel: "Date adopted (resolutions) or issued (documents)",
  datePlaceholder: "YYYY-MM-DD",
  websiteLabel: "Website (for MLA)",
  websitePlaceholder: "United Nations",
  urlLabel: "URL",
  urlPlaceholder: "https://docs.un.org/en/A/RES/70/1",
  urlHint: "The official copy is at docs.un.org/en/ followed by the symbol.",
  apaLabel: "APA 7th edition",
  mlaLabel: "MLA 9th edition",
  chicagoLabel: "Chicago 18th edition (notes)",
  referenceList: "Reference list",
  parenthetical: "In-text citation (parenthetical)",
  narrative: "In-text citation (narrative)",
  worksCited: "Works Cited",
  inText: "In-text citation",
  footnote: "Footnote",
  apaNote:
    "APA 7 has no UN template, so this uses its group-author report form: the document symbol sits where a report number would, and the United Nations is the publisher.",
  mlaNote:
    "MLA styles a resolution's title in roman type, like a law, and italicizes the title of a report.",
  chicagoNote:
    "Chicago (CMOS 18 §14.207) cites UN documents in notes only. If your sentence names the resolution, the note may begin G.A. Res. 70/1 or S.C. Res. 2334.",
  example: {
    body: "ga",
    kind: "resolution",
    number: "70/1",
    title:
      "Transforming our world: the 2030 Agenda for Sustainable Development",
    symbol: "A/RES/70/1",
    date: "2015-09-25",
    website: "United Nations",
    url: "https://docs.un.org/en/A/RES/70/1",
  },
} as const;

type Strings = typeof UN_DOCUMENT_CITATION_GENERATOR_STRINGS;

function buildFields(strings: Strings): SourceField[] {
  return [
    {
      id: F.body,
      kind: "select",
      label: strings.bodyLabel,
      options: [
        { value: "ga", label: strings.bodyGa },
        { value: "sc", label: strings.bodySc },
        { value: "ecosoc", label: strings.bodyEcosoc },
        { value: "hrc", label: strings.bodyHrc },
        { value: "sg", label: strings.bodySg },
      ],
    },
    {
      id: F.kind,
      kind: "select",
      label: strings.kindLabel,
      options: [
        { value: "resolution", label: strings.kindResolution },
        { value: "document", label: strings.kindDocument },
      ],
    },
    {
      id: F.number,
      kind: "text",
      label: strings.numberLabel,
      placeholder: strings.numberPlaceholder,
      hint: strings.numberHint,
    },
    {
      id: F.symbol,
      kind: "text",
      label: strings.symbolLabel,
      placeholder: strings.symbolPlaceholder,
      hint: strings.symbolHint,
    },
    {
      id: F.title,
      kind: "text",
      label: strings.titleLabel,
      placeholder: strings.titlePlaceholder,
      hint: strings.titleHint,
      wide: true,
    },
    {
      id: F.date,
      kind: "date",
      label: strings.dateLabel,
      placeholder: strings.datePlaceholder,
    },
    {
      id: F.shortTitle,
      kind: "text",
      label: strings.shortTitleLabel,
      placeholder: strings.shortTitlePlaceholder,
    },
    {
      id: F.website,
      kind: "text",
      label: strings.websiteLabel,
      placeholder: strings.websitePlaceholder,
    },
    {
      id: F.url,
      kind: "url",
      label: strings.urlLabel,
      placeholder: strings.urlPlaceholder,
      hint: strings.urlHint,
    },
  ];
}

/** The widget's strings, as the formatter's labels. */
export function unDocumentLabels(strings: Strings): UnDocumentLabels {
  return {
    apa: strings.apaLabel,
    mla: strings.mlaLabel,
    chicago: strings.chicagoLabel,
    referenceList: strings.referenceList,
    parenthetical: strings.parenthetical,
    narrative: strings.narrative,
    worksCited: strings.worksCited,
    inText: strings.inText,
    footnote: strings.footnote,
    apaNote: strings.apaNote,
    mlaNote: strings.mlaNote,
    chicagoNote: strings.chicagoNote,
  };
}

export default function UnDocumentCitationGeneratorWidget() {
  const strings = useToolUi(UN_DOCUMENT_CITATION_GENERATOR_STRINGS);
  const fields = useMemo(() => buildFields(strings), [strings]);
  const labels = useMemo(() => unDocumentLabels(strings), [strings]);
  const format = useCallback(
    (values: SourceValues) => formatUnDocument(values, labels),
    [labels]
  );

  return (
    <SourceCitationBuilder
      example={strings.example}
      fields={fields}
      format={format}
      isReady={unDocumentIsReady}
      strings={strings}
    />
  );
}
